import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

interface TourSpotlightProps {
  targetElement: HTMLElement | null;
}

interface SpotlightRect {
  top: number;
  left: number;
  width: number;
  height: number;
}

export function TourSpotlight({ targetElement }: TourSpotlightProps) {
  const [rect, setRect] = useState<SpotlightRect | null>(null);

  useEffect(() => {
    if (!targetElement) {
      setRect(null);
      return;
    }

    const updateRect = () => {
      const bounds = targetElement.getBoundingClientRect();
      const padding = 8;
      
      setRect({
        top: bounds.top - padding + window.scrollY,
        left: bounds.left - padding,
        width: bounds.width + padding * 2,
        height: bounds.height + padding * 2
      });
    };

    updateRect();

    // Scroll element into view
    targetElement.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
      inline: 'center'
    });

    // Update on scroll/resize
    window.addEventListener('scroll', updateRect);
    window.addEventListener('resize', updateRect);
    
    return () => {
      window.removeEventListener('scroll', updateRect);
      window.removeEventListener('resize', updateRect);
    };
  }, [targetElement]);

  if (!rect) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.3 }}
        className="fixed inset-0 z-[9998] pointer-events-none"
        style={{
          background: `
            linear-gradient(to right, rgba(0,0,0,0.75) ${rect.left}px, transparent ${rect.left}px),
            linear-gradient(to left, rgba(0,0,0,0.75) calc(100% - ${rect.left + rect.width}px), transparent calc(100% - ${rect.left + rect.width}px)),
            linear-gradient(to bottom, rgba(0,0,0,0.75) ${rect.top}px, transparent ${rect.top}px),
            linear-gradient(to top, rgba(0,0,0,0.75) calc(100% - ${rect.top + rect.height}px), transparent calc(100% - ${rect.top + rect.height}px))
          `
        }}
      />
      
      {/* Spotlight border/glow */}
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        transition={{ duration: 0.3 }}
        className="fixed z-[9999] pointer-events-none rounded-lg"
        style={{
          top: rect.top,
          left: rect.left,
          width: rect.width,
          height: rect.height,
          boxShadow: '0 0 0 4px hsl(var(--primary)), 0 0 20px hsl(var(--primary)/0.5)',
          border: '2px solid hsl(var(--primary))'
        }}
      />

      {/* Click blocker overlay */}
      <div 
        className="fixed inset-0 z-[9997]" 
        style={{ pointerEvents: 'auto' }}
        onClick={(e) => e.stopPropagation()}
      />
    </AnimatePresence>
  );
}
